/**
 * Billing AI Admin Page (v2 UX)
 *
 * Surfaces the four backend AI/rules services in one place:
 *   - Braided Revenue scanner (rules-based co-billing across ECM+CS+CHW+Doula)
 *   - Doc Completeness checker (structural + Claude scorer per program rubric)
 *   - Claude Auto-Coder (closed-world claim-line suggester)
 *   - Denial Scorer (20-signal weighted scorer, eligibility/modifiers/rules)
 *
 * UX: V2 design tokens (bg-paper, bg-surface, ink, v2border) and tab bar
 * pattern shared with CCHTabbedPages. Org isolation via X-CBO-ID header.
 *
 * Privacy + RAG: Every backend call to Claude is augmented with org-scoped
 * RAG context (Pinecone namespace `org_{cbo_id}`); no cross-org data leaks.
 * Auto-coder is closed-world to the org's program_billing_activities catalog.
 */

import { useState } from 'react';
import { getWeaveinApiUrl } from '@/src/config';
import CapabilityGate from '@/components/common/CapabilityGate';
import { PageHeader, PageContainer } from '@/components/weavein';
import {
  Loader2,
  AlertTriangle,
  CheckCircle,
  X,
  Sparkles,
  FileText,
  Activity,
  Workflow,
  ShieldCheck,
} from 'lucide-react';

interface BraidedSuggestion {
  id: number;
  member_id: number;
  program_id: number;
  suggested_activity_id: number;
  suggested_code: string;
  rationale: string;
  estimated_revenue: number;
  evidence: any;
  status: string;
  created_ts: string;
}

interface DocCheckResult {
  score: number;
  structural_pass: boolean;
  missing_elements: string[];
  suggestions: string[];
  used_llm: boolean;
  verdict: 'pass' | 'warn' | 'block';
}

interface CodingSuggestion {
  billing_activity_id: number;
  activity_code: string;
  cpt_or_hcpcs: string;
  modifiers: string[];
  dx_codes: string[];
  place_of_service: string | null;
  units: number;
  confidence: number;
  rationale: string;
  auto_submit_eligible: boolean;
}

interface DenialReason {
  code: string;
  weight: number;
  severity: string;
  message: string;
}

interface DenialScore {
  score: number;
  verdict: 'allow' | 'warn' | 'block';
  reasons: DenialReason[];
}

type TabId = 'braided' | 'doc' | 'autocode' | 'denial';

// Only Claude models are permitted for Billing AI. Listed in capability order.
const CLAUDE_MODELS = [
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (default)' },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7 (highest quality)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (fastest)' },
];

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function BillingAIPage() {
  const organizationId = Number(localStorage.getItem('active_cbo_id')) || null;
  const [tab, setTab] = useState<TabId>('braided');

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="billing"
      behavior="show-upgrade"
      upgradeMessage="Billing capability is not enabled for your organization."
    >
      <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Billing"
        title="Billing AI"
        description="Claude-powered braided revenue, doc completeness, auto-coding, and denial scoring"
      />
      <PageContainer>
      {/* Privacy banner */}
      <div className="flex items-start gap-2 rounded-lg border border-v2border-muted bg-paper-2 px-4 py-3 text-sm text-ink-500">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-v2accent" />
        <div>
          <span className="font-medium text-ink">Privacy & data scope.</span>{' '}
          All LLM calls run on Claude only, augmented with your organization's RAG namespace
          (<code className="text-[12px]">org_{organizationId ?? '—'}</code>). Auto-coder is
          closed-world to your <em>program_billing_activities</em> catalog. No data leaves your
          tenant.
        </div>
      </div>

      {/* Tab bar (CCHTabbedPages pattern) */}
      <TabBar
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'braided', label: 'Braided Revenue', icon: <Workflow className="h-3.5 w-3.5" /> },
          { id: 'doc', label: 'Doc Completeness', icon: <FileText className="h-3.5 w-3.5" /> },
          { id: 'autocode', label: 'Auto-Coder', icon: <Sparkles className="h-3.5 w-3.5" /> },
          { id: 'denial', label: 'Denial Scorer', icon: <Activity className="h-3.5 w-3.5" /> },
        ]}
      />

      <div>
        {tab === 'braided' && <BraidedRevenueTab />}
        {tab === 'doc' && <DocCompletenessTab />}
        {tab === 'autocode' && <AutoCoderTab />}
        {tab === 'denial' && <DenialScorerTab />}
      </div>
      </PageContainer>
      </div>
    </CapabilityGate>
  );
}

// ============================================================
// Shared v2 primitives
// ============================================================
function TabBar<T extends string>({
  active,
  onChange,
  tabs,
}: {
  active: T;
  onChange: (t: T) => void;
  tabs: { id: T; label: string; icon?: React.ReactNode }[];
}) {
  return (
    <div className="flex gap-1 border-b border-v2border">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
            active === t.id
              ? 'border-indigo-500 font-medium text-indigo-600'
              : 'border-transparent text-ink-500 hover:text-ink'
          }`}
        >
          {t.icon}
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Section({ children, title, hint }: { children: React.ReactNode; title?: string; hint?: string }) {
  return (
    <section className="rounded-lg border border-v2border bg-surface p-4">
      {title && (
        <header className="mb-3">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {hint && <p className="mt-0.5 text-xs text-ink-500">{hint}</p>}
        </header>
      )}
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
      <span>{label}</span>
      {children}
      {hint && <span className="text-[11px] font-normal text-ink-400">{hint}</span>}
    </label>
  );
}

const inputCls =
  'rounded-md border border-v2border bg-paper px-3 py-1.5 text-sm text-ink focus:border-v2accent focus:outline-none focus:ring-1 focus:ring-v2accent';
const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50';
const btnSecondary =
  'inline-flex items-center gap-1.5 rounded-md border border-v2border bg-paper px-3 py-1.5 text-sm font-medium text-ink hover:bg-paper-2 disabled:opacity-50';

// ============================================================
// Braided Revenue
// ============================================================
function BraidedRevenueTab() {
  const [memberId, setMemberId] = useState<string>('');
  const [lookback, setLookback] = useState<number>(30);
  const [running, setRunning] = useState(false);
  const [suggestions, setSuggestions] = useState<BraidedSuggestion[]>([]);
  const [statusFilter, setStatusFilter] = useState<'open' | 'actioned' | 'dismissed'>('open');
  const [error, setError] = useState<string | null>(null);

  async function runScan() {
    setRunning(true);
    setError(null);
    try {
      const params = new URLSearchParams({ lookback_days: String(lookback) });
      if (memberId.trim()) params.set('member_id', memberId.trim());
      const r = await fetch(getWeaveinApiUrl(`/api/billing-ai/braided-revenue/scan?${params}`), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      await loadList();
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  async function loadList() {
    const params = new URLSearchParams({ status: statusFilter });
    if (memberId.trim()) params.set('member_id', memberId.trim());
    const r = await fetch(getWeaveinApiUrl(`/api/billing-ai/braided-revenue?${params}`), {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (r.ok) setSuggestions(await r.json());
  }

  async function actionOne(id: number, action: 'accept' | 'dismiss') {
    const reason = action === 'dismiss' ? prompt('Reason for dismissing this suggestion?') : null;
    const r = await fetch(getWeaveinApiUrl(`/api/billing-ai/braided-revenue/${id}/action`), {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ action, reason }),
    });
    if (r.ok) await loadList();
  }

  return (
    <Section
      title="Braided revenue scan"
      hint="Identifies missed co-billing opportunities across members enrolled in 2+ CalAIM programs (ECM, CS, CHW, Doula). Rule-based; no LLM calls."
    >
      <div className="flex flex-wrap items-end gap-3 pb-3">
        <Field label="Member ID (optional)">
          <input
            type="number"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className={`${inputCls} w-32`}
          />
        </Field>
        <Field label="Lookback (days)">
          <input
            type="number"
            value={lookback}
            min={7}
            max={365}
            onChange={(e) => setLookback(Number(e.target.value) || 30)}
            className={`${inputCls} w-24`}
          />
        </Field>
        <button className={btnPrimary} disabled={running} onClick={runScan}>
          {running && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Run scan
        </button>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className={inputCls}
        >
          <option value="open">Open</option>
          <option value="actioned">Actioned</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <button className={btnSecondary} onClick={loadList}>
          Refresh
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="overflow-hidden rounded-md border border-v2border-muted">
        <table className="w-full text-sm">
          <thead className="bg-paper-2 text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-3 py-2 text-left">Member</th>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Rationale</th>
              <th className="px-3 py-2 text-right">Est. revenue</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-v2border-muted">
            {suggestions.map((s) => (
              <tr key={s.id} className="hover:bg-paper-2/50">
                <td className="px-3 py-2 text-ink">{s.member_id}</td>
                <td className="px-3 py-2">
                  <code className="rounded bg-paper-2 px-1.5 py-0.5 text-[12px] text-ink">
                    {s.suggested_code}
                  </code>
                </td>
                <td className="px-3 py-2 text-ink-500">{s.rationale}</td>
                <td className="px-3 py-2 text-right font-medium text-ink">
                  ${Number(s.estimated_revenue || 0).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right">
                  {s.status === 'open' ? (
                    <div className="flex justify-end gap-1.5">
                      <button className={btnPrimary} onClick={() => actionOne(s.id, 'accept')}>
                        Accept
                      </button>
                      <button className={btnSecondary} onClick={() => actionOne(s.id, 'dismiss')}>
                        Dismiss
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs uppercase text-ink-400">{s.status}</span>
                  )}
                </td>
              </tr>
            ))}
            {suggestions.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-sm text-ink-500">
                  No suggestions yet. Click <b>Run scan</b> to identify missed revenue.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ============================================================
// Doc Completeness
// ============================================================
function DocCompletenessTab() {
  const [note, setNote] = useState('');
  const [programType, setProgramType] = useState('ECM');
  const [activityCode, setActivityCode] = useState('');
  const [useLlm, setUseLlm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DocCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runCheck() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetch(getWeaveinApiUrl('/api/billing-ai/doc-completeness/check'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          note,
          program_type: programType,
          activity_code: activityCode || null,
          use_llm: useLlm,
        }),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setResult(await r.json());
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const verdictColor =
    result?.verdict === 'pass'
      ? 'text-emerald-600'
      : result?.verdict === 'warn'
      ? 'text-amber-600'
      : 'text-red-600';

  return (
    <Section
      title="Documentation completeness"
      hint="Scores a clinical/service note against the program-specific MCP audit rubric. Structural pass uses keyword/length checks. LLM refinement uses Claude (RAG-augmented with your org's ECM/CS/Doula policy docs)."
    >
      <div className="flex flex-wrap items-end gap-3 pb-3">
        <Field label="Program type">
          <select
            value={programType}
            onChange={(e) => setProgramType(e.target.value)}
            className={inputCls}
          >
            <option>ECM</option>
            <option>CS</option>
            <option>CHW</option>
            <option>Doula</option>
          </select>
        </Field>
        <Field label="Activity code (optional)">
          <input
            value={activityCode}
            onChange={(e) => setActivityCode(e.target.value)}
            className={`${inputCls} w-32`}
          />
        </Field>
        <label className="flex items-center gap-2 pb-1.5 text-xs text-ink-500">
          <input
            type="checkbox"
            checked={useLlm}
            onChange={(e) => setUseLlm(e.target.checked)}
            className="h-3.5 w-3.5 accent-v2accent"
          />
          Use Claude refinement for borderline scores
        </label>
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Paste the clinical/service note here…"
        className={`${inputCls} min-h-[160px] w-full font-mono text-[13px]`}
      />
      <div className="mt-2">
        <button className={btnPrimary} disabled={busy || !note.trim()} onClick={runCheck}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Score note
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {result && (
        <div className="mt-4 rounded-md border border-v2border-muted bg-paper-2 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              Score:{' '}
              <span className={verdictColor}>
                {(result.score * 100).toFixed(0)}% — {result.verdict.toUpperCase()}
              </span>
            </h3>
            {result.used_llm && (
              <span className="rounded-full bg-v2accent-soft px-2 py-0.5 text-[11px] font-medium text-v2accent">
                Claude-refined
              </span>
            )}
          </div>
          {result.missing_elements.length > 0 && (
            <p className="mt-2 text-sm text-ink-500">
              <strong className="text-ink">Missing:</strong> {result.missing_elements.join(', ')}
            </p>
          )}
          {result.suggestions.length > 0 && (
            <>
              <p className="mt-2 text-sm font-medium text-ink">Improvements:</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-ink-500">
                {result.suggestions.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

// ============================================================
// Auto-Coder
// ============================================================
function AutoCoderTab() {
  const [note, setNote] = useState('');
  const [programId, setProgramId] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState<CodingSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function runCode() {
    if (!programId.trim()) {
      setError('Program ID is required');
      return;
    }
    setBusy(true);
    setError(null);
    setSuggestions([]);
    try {
      const r = await fetch(getWeaveinApiUrl('/api/billing-ai/auto-code'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          note,
          program_id: Number(programId),
          duration_minutes: duration ? Number(duration) : null,
          model,
        }),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const data = await r.json();
      setSuggestions(data.suggestions || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Claim-line auto-coder"
      hint="Closed-world Claude coder. Only suggests activities from your program's catalog. Augmented with your org's RAG namespace (policy docs, prior-claim patterns, denial history). Confidence ≥ 0.7 is auto-submit eligible; below requires human review."
    >
      <div className="flex flex-wrap items-end gap-3 pb-3">
        <Field label="Program ID *">
          <input
            type="number"
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            className={`${inputCls} w-24`}
          />
        </Field>
        <Field label="Duration (min)">
          <input
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className={`${inputCls} w-24`}
          />
        </Field>
        <Field label="Claude model">
          <select value={model} onChange={(e) => setModel(e.target.value)} className={inputCls}>
            {CLAUDE_MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Paste the clinical/service note here…"
        className={`${inputCls} min-h-[160px] w-full font-mono text-[13px]`}
      />
      <div className="mt-2">
        <button className={btnPrimary} disabled={busy || !note.trim()} onClick={runCode}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Suggest coding
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {suggestions.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-md border border-v2border-muted">
          <table className="w-full text-sm">
            <thead className="bg-paper-2 text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Activity</th>
                <th className="px-3 py-2 text-left">Modifiers</th>
                <th className="px-3 py-2 text-left">Dx</th>
                <th className="px-3 py-2 text-left">POS</th>
                <th className="px-3 py-2 text-right">Units</th>
                <th className="px-3 py-2 text-right">Confidence</th>
                <th className="px-3 py-2 text-left">Rationale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-v2border-muted">
              {suggestions.map((s, i) => (
                <tr key={i} className="hover:bg-paper-2/50">
                  <td className="px-3 py-2">
                    <code className="rounded bg-paper-2 px-1.5 py-0.5 text-[12px] text-ink">
                      {s.cpt_or_hcpcs}
                    </code>
                  </td>
                  <td className="px-3 py-2 text-ink">{s.activity_code}</td>
                  <td className="px-3 py-2 text-ink-500">{s.modifiers.join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-ink-500">{s.dx_codes.join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-ink-500">{s.place_of_service || '—'}</td>
                  <td className="px-3 py-2 text-right text-ink">{s.units}</td>
                  <td
                    className={`px-3 py-2 text-right font-medium ${
                      s.auto_submit_eligible ? 'text-emerald-600' : 'text-amber-600'
                    }`}
                  >
                    {(s.confidence * 100).toFixed(0)}%
                  </td>
                  <td className="px-3 py-2 text-ink-500">{s.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ============================================================
// Denial Scorer
// ============================================================
function DenialScorerTab() {
  const [encounterId, setEncounterId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DenialScore | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runScore() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetch(
        getWeaveinApiUrl(`/api/service-encounters/${encounterId}/denial-score`),
        { method: 'POST', credentials: 'include', headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      setResult(await r.json());
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const verdictColor =
    result?.verdict === 'allow'
      ? 'text-emerald-600'
      : result?.verdict === 'warn'
      ? 'text-amber-600'
      : 'text-red-600';

  return (
    <Section
      title="Denial-risk scorer"
      hint="Pre-submission denial probability across 20 weighted signals (eligibility freshness, MCP rule fit, add-on coding, timely filing, unit limits, prior auth). Rule-based; no LLM call. Threshold ≥ 0.40 → block, ≥ 0.15 → warn."
    >
      <div className="flex flex-wrap items-end gap-3 pb-3">
        <Field label="Encounter ID">
          <input
            type="number"
            value={encounterId}
            onChange={(e) => setEncounterId(e.target.value)}
            className={`${inputCls} w-32`}
          />
        </Field>
        <button className={btnPrimary} disabled={busy || !encounterId} onClick={runScore}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Score denial risk
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {result && (
        <div className="mt-4 rounded-md border border-v2border-muted bg-paper-2 p-4">
          <h3 className="text-sm font-semibold text-ink">
            Denial probability:{' '}
            <span className={verdictColor}>
              {(result.score * 100).toFixed(0)}% — {result.verdict.toUpperCase()}
            </span>
          </h3>
          {result.reasons.length === 0 ? (
            <p className="mt-2 flex items-center gap-1 text-sm text-emerald-600">
              <CheckCircle className="h-3.5 w-3.5" />
              No risk signals detected.
            </p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="py-1.5 pr-3 text-left">Severity</th>
                  <th className="py-1.5 pr-3 text-left">Code</th>
                  <th className="py-1.5 pr-3 text-right">Weight</th>
                  <th className="py-1.5 text-left">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {result.reasons.map((r, i) => {
                  const sevCls =
                    r.severity === 'blocker'
                      ? 'text-red-600'
                      : r.severity === 'warning'
                      ? 'text-amber-600'
                      : 'text-ink-400';
                  const Icon =
                    r.severity === 'blocker' ? X : r.severity === 'warning' ? AlertTriangle : CheckCircle;
                  return (
                    <tr key={i}>
                      <td className={`py-1.5 pr-3 ${sevCls}`}>
                        <Icon className="mr-1 inline h-3.5 w-3.5" />
                        {r.severity}
                      </td>
                      <td className="py-1.5 pr-3">
                        <code className="rounded bg-paper px-1.5 py-0.5 text-[12px]">{r.code}</code>
                      </td>
                      <td className="py-1.5 pr-3 text-right text-ink">{r.weight.toFixed(2)}</td>
                      <td className="py-1.5 text-ink-500">{r.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Section>
  );
}
